import { Component, EventEmitter, Output, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { PermissionsModuleAccessLevel } from '@predictiveindex/client-permissions-lib';
import {
    design2Home,
    dreamTeamsRoutePath,
    dreamTeamsSelfServeRoutePath,
    designExplainerPath,
    hireExplainerPath,
    diagnoseExplainerPath,
    inspireRoutePath
} from '../constants-types';
import { Hire2Link } from '../constants-types/constants';
import {
    NavOptions,
    OnboardingChecklistRoute,
    RoleType
} from '../constants-types/Enums';
import { IconSize } from '../core-data-model/IconSize';
import { IconType } from '../core-data-model/IconType';
import { ModuleName } from '../core-data-model/ModuleAccess';
import { SiteNavigationService } from '../core-services';
import { OnboardingService } from '../core-services/Onboarding.service';
import { GlobalNavService } from '../core-ui/global-nav/global-nav.service';
import {
    FeatureFlags,
    FeatureFlagsService
} from '../system-services/FeatureFlags.service';
import { InitDataService } from '../system-services/InitData/InitData.service';
import { ModuleAccessService } from '../system-services/ModuleAccess/ModuleAccess.service';
import { ModuleUserAccessService } from '../system-services/ModuleUserAccess.service';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings/UserProfileSettings.service';
import { TranslationService } from '../translations/Translation.service';
import { ProductNavigationMenu } from './mini-navigation-dropdown.component';

/**
 * The legacy 1.0 navigation menu. This should only be able to be navigated to
 * in 1.0 pi-spa.
 */
@Component({
    selector: 'pi-navigation-menu',
    template: `
        <div
            class="piNavMenu"
            [ngClass]="{
                'piNavMenu--Hide': hideNav,
                'piNavMenu--open': mobileNavOpen
            }"
            (mouseenter)="hideNav = false"
        >
            <a
                class="piNavMenu--Toggle button button-secondary"
                (click)="toggleMobileNavOpen()"
            >
                <i class="fa fa-bars"></i>
                <span>{{ TR.key('label.menu.responsive.toggle') }}</span>
            </a>
            @if (!this.initData.isAbacUser()) {
                <ul>
                    @if (
                        !isDesignStandalone &&
                        !isHireHeroStandalone &&
                        !isDiagnoseSelfServe
                    ) {
                        <li
                            data-testing="browseDropdownMenu"
                            class="moduleTabs"
                        >
                            @if (!isDesignStandalone) {
                                <a>
                                    {{ TR.key('label.menu.browse.header') }}
                                    <abstract-icon
                                        [iconType]="IconType.ArrowDownThin"
                                        class="piNavMenuHeading--Icon"
                                    >
                                    </abstract-icon>
                                </a>
                            }
                            <mini-navigation-dropdown
                                [productNavigationMenu]="
                                    ProductNavigationMenu.Browse
                                "
                                [isOrganizationEnabled]="isOrganizationEnabled"
                                [isReadOnly]="isReadOnly"
                            ></mini-navigation-dropdown>
                        </li>
                    }
                    @if (showOnboardingMenu()) {
                        <li
                            class="piNavMegaMenu-menuHeader piNavMegaMenu-menuHeader--alt"
                            data-testing="navOnboarding"
                            (click)="onboardingNavigate()"
                        >
                            <a>{{ TR.key('navigation.onboardingNextStep') }}</a>
                        </li>
                    }
                    <!-- Design Navigation -->
                    <li
                        class="moduleTabs"
                        [ngClass]="{
                            inactiveModule: !isDesignNavigationEnabled
                        }"
                        data-testing="designMenuSectionHeader"
                    >
                        <a
                            [routerLink]="designRouterLink"
                            [ngClass]="
                                isDesignNavigationEnabled
                                    ? 'cursorPointer'
                                    : 'disable-interaction'
                            "
                        >
                            {{ TR.key('label.common-terms.design') }}
                        </a>
                    </li>
                    <!-- Hire Navigation -->
                    @if (isHire2NavigationEnabled) {
                        <li
                            [ngClass]="{
                                inactiveModule: !isHire2NavigationEnabled
                            }"
                            data-testing="hire2MenuSectionHeader"
                        >
                            <a
                                [ngClass]="
                                    isHire2NavigationEnabled
                                        ? 'cursorPointer'
                                        : 'disable-interaction'
                                "
                                [routerLink]="hireRouterLink"
                            >
                                {{ TR.key('label.common-terms.hire') }}
                            </a>
                        </li>
                    } @else {
                        <li
                            class="moduleTabs"
                            [ngClass]="{
                                inactiveModule: !hasHire
                            }"
                            data-testing="hireMenuSectionHeader"
                        >
                            <a>
                                {{ TR.key('label.common-terms.hire') }}
                                <abstract-icon
                                    [iconType]="IconType.ArrowDownThin"
                                    class="piNavMenuHeading--Icon"
                                >
                                </abstract-icon>
                            </a>
                            <mini-navigation-dropdown
                                [productNavigationMenu]="
                                    ProductNavigationMenu.Hire
                                "
                                [isReadOnly]="isReadOnly"
                                [moduleIsActive]="hasHire"
                            ></mini-navigation-dropdown>
                        </li>
                    }
                    <!-- Inspire Navigation -->
                    @if (isNewInspireNavigationActive) {
                        <li>
                            <a
                                [routerLink]="inspireRouterLink"
                                class="cursorPointer"
                            >
                                {{ TR.key('label.common-terms.inspire') }}
                            </a>
                        </li>
                    } @else {
                        <li
                            class="moduleTabs"
                            [ngClass]="{
                                inactiveModule: !hasInspire
                            }"
                            data-testing="inspireMenuSectionHeader"
                        >
                            <a>
                                {{ TR.key('label.common-terms.inspire') }}
                                <abstract-icon
                                    [iconType]="IconType.ArrowDownThin"
                                    class="piNavMenuHeading--Icon"
                                >
                                </abstract-icon>
                            </a>
                            <mini-navigation-dropdown
                                [productNavigationMenu]="
                                    ProductNavigationMenu.Inspire
                                "
                                [moduleIsActive]="hasInspire"
                            ></mini-navigation-dropdown>
                        </li>
                    }
                    <!-- Diagnose Navigation -->
                    @if (isNewDiagnoseNavigationActive) {
                        <li data-testing="diagnoseMenuSectionHeader">
                            <a
                                [routerLink]="diagnoseRouterLink"
                                class="cursorPointer"
                            >
                                {{ TR.key('label-common-terms.engage') }}
                            </a>
                        </li>
                    } @else {
                        @if (
                            isAccountAdminOrAbove && !hasNewDiagnoseExperience
                        ) {
                            <li
                                class="moduleTabs"
                                [ngClass]="{
                                    inactiveModule: !hasEngage
                                }"
                                data-testing="engageMenuSectionHeader"
                            >
                                <a
                                    >{{ TR.key('label-common-terms.engage') }}
                                    <abstract-icon
                                        [iconType]="IconType.ArrowDownThin"
                                        class="piNavMenuHeading--Icon"
                                    >
                                    </abstract-icon>
                                </a>
                                <div>
                                    <mini-navigation-dropdown
                                        [productNavigationMenu]="
                                            ProductNavigationMenu.Diagnose
                                        "
                                        [moduleIsActive]="hasEngage"
                                    ></mini-navigation-dropdown>
                                </div>
                            </li>
                        }
                    }
                    <!-- Perform Navigation -->
                    @if (isPerformNavigationEnabled) {
                        <li
                            [ngClass]="{
                                inactiveModule: isPerformNavInteractionDisabled
                            }"
                            data-testing="performMenuSectionHeader"
                            tooltip
                            [tooltipContent]="performNavDisableTooltipContent"
                            [tooltipDisabled]="!isPerformNavInteractionDisabled"
                            tooltipPlacement="bottom"
                            (click)="navigateToPerform()"
                        >
                            <a
                                [ngClass]="
                                    isPerformNavInteractionDisabled
                                        ? 'disable-interaction'
                                        : 'cursorPointer'
                                "
                            >
                                {{ TR.key('label.common-terms.perform') }}
                                @if (showPerformNavigationIcon) {
                                    <abstract-icon
                                        [iconType]="IconType.LearnNew"
                                        [iconSize]="13"
                                        class="learnIcon"
                                    >
                                    </abstract-icon>
                                }
                            </a>
                        </li>
                    }
                    <!-- Learn Navigation -->
                    @if (
                        !isTeamDiscoverySelfServe && !hasNewDiagnoseExperience
                    ) {
                        <li data-testing="learnButton">
                            <a
                                href="https://www.predictiveindex.com/learn/login/?utm_source=platform&utm_medium=header-nav"
                                class="cursorPointer"
                                target="_blank"
                            >
                                {{ TR.key('label.common-terms.learn') }}
                                <abstract-icon
                                    [iconType]="IconType.LearnNew"
                                    [iconSize]="13"
                                    class="learnIcon"
                                >
                                </abstract-icon>
                            </a>
                        </li>
                    }
                </ul>
            }
        </div>
        @if (!showUpgradeButton()) {
            <div class="piNavMenu--Aside">
                <div class="piNavMenu--action">
                    @if (
                        isUserOrAbove &&
                        !isDesignStandalone &&
                        !hasHireHero &&
                        !isDiagnoseStandalone
                    ) {
                        <a
                            data-testing="assessmentCenterButton"
                            class="button button-primary button-sm"
                            routerLink="/assessmentCenter"
                        >
                            {{ TR.key('common.SendAssessments') }}
                        </a>
                    }
                </div>
                <div
                    class="piNavMenu--action"
                    tooltip
                    [tooltipContent]="inactiveTooltip"
                    [tooltipDisabled]="enableCreateJobButton"
                    tooltipPlacement="bottom"
                >
                    @if (
                        isUserOrAbove &&
                        !isDesignStandalone &&
                        !hasHireHero &&
                        !isDiagnoseStandalone
                    ) {
                        <button
                            class="button button-primary button-sm"
                            [ngClass]="{
                                overrideDisabled:
                                    !hasHire && !enableCreateJobButton
                            }"
                            data-testing="createNewJobButton"
                            routerLink="/createJob"
                            routerLinkActive="active"
                            [disabled]="!enableCreateJobButton"
                        >
                            {{ TR.key('navigation.create-job') }}
                        </button>
                    }
                </div>
                <tooltip-content #inactiveTooltip>
                    <inactive-feature-tooltip-content></inactive-feature-tooltip-content>
                </tooltip-content>
            </div>
        } @else {
            <div class="piNavMenu--Aside">
                <div class="piNavMenu--action">
                    @if (showUpgradeButton()) {
                        <upgrade-account
                            (onUpgradeAccountClick)="
                                triggerUpgradeAccountClick()
                            "
                            [showUpgradeButtonIcon]="showUpgradeButtonIcon"
                            [buttonTextKeyName]="buttonTextKeyName"
                        ></upgrade-account>
                    }
                </div>
            </div>
        }
    `,
    styles: [
        `
            .overrideDisabled {
                cursor: default;
            }
            .moduleTabs {
                position: relative;
            }
            .cursorPointer {
                cursor: pointer;
                pointer-events: auto;
                display: flex;
            }
            .learnIcon {
                margin-left: 2px;
                align-self: center;
                stroke: #91899e;
            }
        `
    ],
    standalone: false
})
export class LegacyNavigationMenuComponent implements OnInit {

    @Input()
    buttonTextKeyName: string;

    @Input()
    showUpgradeButtonIcon: boolean;

    @Output()
    onUpgradeAccountClick: EventEmitter<any> = new EventEmitter();

    hideNav = false;
    mobileNavOpen = false;
    OnboardingChecklistRoute: OnboardingChecklistRoute;
    IconType = IconType;
    IconSize = IconSize;
    ProductNavigationMenu = ProductNavigationMenu;
    PermissionsModuleAccessLevel = PermissionsModuleAccessLevel;
    ModuleName = ModuleName;

    isReadOnly: boolean;
    isAccountAdminOrAbove: boolean;
    isOrganizationEnabled: boolean;
    isPerformNavInteractionDisabled: boolean;

    constructor(
        public TR: TranslationService,
        public initData: InitDataService,
        private moduleAccess: ModuleAccessService,
        public featureFlags: FeatureFlagsService,
        public onboardingService: OnboardingService,
        public userProfile: UserProfileSettingsService,
        private router: Router,
        public siteNavigationService: SiteNavigationService,
        private moduleUserAccessService: ModuleUserAccessService,
        private globalNavService: GlobalNavService
    ) {}

    get enableCreateJobButton() {
        return this.initData.account.hasHire;
    }

    get isUserOrAbove() {
        return this.initData.user.hasPermissionLevelOrGreater(RoleType.User);
    }

    get isReadOnlyOrAbove() {
        return this.initData.user.hasPermissionLevelOrGreater(
            RoleType.ReadOnly
        );
    }

    get designRouterLink() {
        if (this.moduleAccess.isTeamDiscoverySelfServe) {
            return `/design/${dreamTeamsSelfServeRoutePath}`;
        } else if (
            this.moduleAccess.hasDesign2 &&
            this.moduleUserAccessService.getUserAccessLevel(
                ModuleName.Design
            ) !== PermissionsModuleAccessLevel.Unknown
        ) {
            return `/${design2Home}`;
        } else if (this.moduleAccess.hasDesign1) {
            return `/design/${dreamTeamsRoutePath}`;
        } else {
            // This will only be able to be navigated to if the explainer pages
            // are enabled for Design. See `isDesignNavigationEnabled`
            return `${designExplainerPath}`;
        }
    }

    get hireRouterLink() {
        if (this.siteNavigationService.isInHire) {
            return this.siteNavigationService.getBaseRoute();
        } else {
            if (
                !this.hasHireHero ||
                this.moduleUserAccessService.getUserAccessLevel(
                    ModuleName.Hire
                ) === PermissionsModuleAccessLevel.Unknown
            ) {
                return `${hireExplainerPath}`;
            } else {
                return `${Hire2Link}`;
            }
        }
    }

    // inspire IA is "off"
    get inspireRouterLink() {
        return `${inspireRoutePath}`;
    }

    get diagnoseRouterLink() {
        return this.hasNewDiagnoseExperience &&
            this.moduleUserAccessService.getUserAccessLevel(
                ModuleName.Diagnose
            ) !== PermissionsModuleAccessLevel.Unknown
            ? 'diagnose'
            : `${diagnoseExplainerPath}`;
    }

    get hasHire() {
        return this.moduleAccess.hasHire;
    }

    get hasInspire() {
        return this.moduleAccess.hasInspire;
    }

    get hasEngage() {
        return this.moduleAccess.hasDiagnose;
    }

    get isDiagnoseSelfServe() {
        return this.moduleAccess.isDiagnoseSelfServe;
    }

    get hasNewDiagnoseExperience() {
        return (
            this.moduleAccess.isDiagnoseSelfServe &&
            this.moduleAccess.hasDiagnose
        );
    }

    get hasDesign() {
        return (
            this.isUserOrAbove &&
            (this.moduleAccess.hasDesign || this.moduleAccess.hasDesign2)
        );
    }

    get isDesignStandalone() {
        return this.moduleAccess.isDesignStandalone;
    }

    get isTeamDiscoverySelfServe() {
        return this.moduleAccess.isTeamDiscoverySelfServe;
    }

    get isHireHeroStandalone() {
        return this.moduleAccess.isHireHeroStandalone;
    }

    get isDiagnoseStandalone() {
        return this.moduleAccess.isDiagnoseStandalone;
    }

    get isHireHeroSelfServe() {
        return this.moduleAccess.isHireHeroSelfServe;
    }

    get hasHireHero() {
        return (
            !this.initData.isAbacUser() && this.moduleAccess.isHireHeroSelfServe
        );
    }

    get hasUserDesignModuleAccess() {
        return this.moduleAccess.isTeamDiscoverySelfServe ||
            this.moduleAccess.hasDesign2
            ? this.moduleUserAccessService.hasBasicDesignPermissions()
            : this.hasDesign;
    }

    get hasProductLedGrowthModule() {
        return this.hasHireHero || this.moduleAccess.hasDesign2;
    }

    get hasUserHireModuleAccess() {
        return (
            this.hasHireHero &&
            this.moduleUserAccessService.hasBasicHirePermissions()
        );
    }

    get isDesignNavigationEnabled() {
        return this.hasUserDesignModuleAccess || this.hasProductLedGrowthModule;
    }

    get isHire2NavigationEnabled() {
        return (
            this.hasHireHero || (!this.hasHire && this.hasProductLedGrowthModule)
        );
    }

    get isNewInspireNavigationActive() {
        return this.moduleAccess.hasInspire2;
    }

    get isNewDiagnoseNavigationActive() {
        return this.hasNewDiagnoseExperience;
    }

    /**
     * See the complimentary method in `global-nav-service.ts`, `shouldDisplayModule` for 2.0 pi-platform.
     * A rollout is planned for Third Party User Support
     */
    get isPerformNavigationEnabled() {
        return true;
    }

    get showPerformNavigationIcon(): boolean {
        return !!this.globalNavService.getModuleNavigationDetails(
            NavOptions.Perform
        ).icon;
    }

    get performNavDisableTooltipContent() {
        return this.globalNavService.getModuleNavigationDetails(
            NavOptions.Perform
        ).tooltipContent;
    }

    ngOnInit() {
        this.isReadOnly = this.initData.user.isReadOnly();

        this.isAccountAdminOrAbove =
            this.initData.user.hasPermissionLevelOrGreater(
                RoleType.AccountAdmin
            );

        this.isOrganizationEnabled = false;

        if (
            this.isAccountAdminOrAbove &&
            (this.featureFlags.getByFlag(FeatureFlags.orgUploadAccess) ||
                this.initData.account.isOnboardingEnabled ||
                this.initData.account.hasEngage)
        ) {
            this.isOrganizationEnabled = true;
        }

        this.isPerformNavInteractionDisabled =
            this.globalNavService.getModuleNavigationDetails(
                NavOptions.Perform
            ).isInteractionDisabled;
    }

    navigateToPerform() {
        this.globalNavService
            .getModuleNavigationDetails(NavOptions.Perform)
            .onClickAction();
    }

    toggleMobileNavOpen() {
        this.mobileNavOpen = !this.mobileNavOpen;
    }

    hideNavMenu() {
        this.hideNav = true;
    }

    onboardingNavigate(): void {
        this.router.navigate([
            this.onboardingService.onboardingNextStepRoute()
        ]);
    }

    showOnboardingMenu() {
        return (
            !this.moduleAccess.hasAnySelfServeModule &&
            this.featureFlags.getByFlag(FeatureFlags.onboardingChecklist) &&
            this.TR.isEnglish() &&
            this.userProfile.inMyOwnCompany() &&
            !this.isDesignStandalone &&
            !this.isHireHeroStandalone
        );
    }

    triggerUpgradeAccountClick() {
        if (this.siteNavigationService.isInHire) {
            this.onUpgradeAccountClick.emit();
        }
    }



    showUpgradeButton(): boolean {
        return (
            (this.hasHireHero && this.siteNavigationService.isInHire) ||
            (this.hasUserDesignModuleAccess &&
                this.siteNavigationService.isInDesign &&
                !this.moduleAccess.hasDesign1)
        );
    }
}
